import React, { useEffect, useMemo, useState } from "react";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Container,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../../../models/SortType";
import CustomerAPI from "../../../api/CustomerAPI";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppSearchDropDown from "../../../../../views/components/common/filter/AppSearchDropDown";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import StorageCostListingTable from "../../components/storage-cost/StorageCostListingTable";
import { StorageCostListItem } from "../../../../../models/StorageCost/StorageCost";
import { StorageCostFilters } from "../../../../../models/StorageCost/StorageCostFilters";

const customerAPI = new CustomerAPI();
const CUSTOMER_SEARCH_DEBOUNCE_MS = 400;
const getDefaultCostDateFrom = () => dayjs().subtract(29, "day");
const getDefaultCostDateTo = () => dayjs();

const StorageCostListing: React.FC = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const { userStore, storageCostStore } = useStores();
  const searchParams = new URLSearchParams(location.search);

  const [isLoading, setIsLoading] = useState(false);
  const parseUrlParams = (): StorageCostFilters => {
    const costDateFromParam = searchParams.get("cost_date_from");
    const costDateToParam = searchParams.get("cost_date_to");
    const parsedCostDateFrom = costDateFromParam
      ? dayjs(costDateFromParam)
      : null;
    const parsedCostDateTo = costDateToParam ? dayjs(costDateToParam) : null;

    return {
      customerName: searchParams.get("customer_name") || null,
      costDateFrom: parsedCostDateFrom?.isValid()
        ? parsedCostDateFrom
        : getDefaultCostDateFrom(),
      costDateTo: parsedCostDateTo?.isValid()
        ? parsedCostDateTo
        : getDefaultCostDateTo(),
      searchText: searchParams.get("search_text") || "",
      sortBy: searchParams.get("sort_by") || "created_date",
      sortType:
        searchParams.get("sort_type") === SortType.ASC
          ? SortType.ASC
          : SortType.DESC,
    };
  };

  const initialFilters: StorageCostFilters = parseUrlParams();
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [draftFilters, setDraftFilters] =
    useState<StorageCostFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<StorageCostFilters>(initialFilters);

  const selectedCustomerOption = useMemo(() => {
    if (!draftFilters.customerName) return [];
    const matchedOption = storageCostStore.customerOptions.find(
      (item) => item.value === draftFilters.customerName,
    );
    return matchedOption
      ? [matchedOption]
      : [{ id: draftFilters.customerName, value: draftFilters.customerName }];
  }, [draftFilters.customerName, storageCostStore.customerOptions]);

  const debouncedCustomerSearch = useDebounce(
    customerSearchText.trim(),
    CUSTOMER_SEARCH_DEBOUNCE_MS,
  );

  const handleCustomerSearch = (value?: string) => {
    setCustomerSearchText(value || "");
  };

  const fetchStorageCosts = async (activeFilters: StorageCostFilters) => {
    try {
      setIsLoading(true);
      await storageCostStore.getStorageCostListing({
        sort_by: activeFilters.sortBy,
        sort_type: activeFilters.sortType || SortType.DESC,
        search_text: activeFilters.searchText || undefined,
        customer_name: activeFilters.customerName || undefined,
        cost_date_from: activeFilters.costDateFrom?.format("YYYY-MM-DD"),
        cost_date_to: activeFilters.costDateTo?.format("YYYY-MM-DD"),
      });
    } catch (error: any) {
      showError(
        "Error",
        error?.response?.data?.message ||
          "Unable to fetch storage cost data. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedCustomerSearch.length < 3) {
      storageCostStore.setCustomerOptions([]);
      return;
    }
    const searchCustomers = async () => {
      try {
        const customerResponse = await customerAPI.getCustomers({
          page_no: 1,
          limit: 30,
          active_status: "active",
          search_text: debouncedCustomerSearch,
        });
        const rows = Array.isArray(customerResponse?.list)
          ? customerResponse.list
          : [];
        storageCostStore.setCustomerOptions(
          rows
            .map((item) => ({
              id: item.name || "",
              value: item.name || "",
            }))
            .filter((item) => item.value),
        );
      } catch (error: any) {
        showError(
          "Error",
          error?.response?.data?.message ||
            "Unable to fetch customers. Please try again.",
        );
      }
    };
    searchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCustomerSearch]);

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_STORAGE_COST])) return;
    fetchStorageCosts(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("sort_by", appliedFilters.sortBy);
    queryParams.set("sort_type", appliedFilters.sortType || SortType.DESC);
    queryParams.set(
      "cost_date_from",
      appliedFilters.costDateFrom?.format("YYYY-MM-DD") || "",
    );
    queryParams.set(
      "cost_date_to",
      appliedFilters.costDateTo?.format("YYYY-MM-DD") || "",
    );
    if (appliedFilters.searchText.trim())
      queryParams.set("search_text", appliedFilters.searchText.trim());
    if (appliedFilters.customerName)
      queryParams.set("customer_name", appliedFilters.customerName);
    const nextSearch = `?${queryParams.toString()}`;
    if (nextSearch !== location.search) {
      navigate({ search: nextSearch }, { replace: true });
    }
  }, [navigate, location.search, appliedFilters]);

  const openDetail = (row: StorageCostListItem) => {
    const costDateFrom = (
      appliedFilters.costDateFrom || getDefaultCostDateFrom()
    ).format("YYYY-MM-DD");
    const costDateTo = (
      appliedFilters.costDateTo || getDefaultCostDateTo()
    ).format("YYYY-MM-DD");
    const detailQuery = new URLSearchParams();
    detailQuery.set("cost_date_from", costDateFrom);
    detailQuery.set("cost_date_to", costDateTo);
    detailQuery.set("parent_query", location.search.replace(/^\?/, ""));
    navigate(
      `/inventory/storage-cost-details/${row.id}?${detailQuery.toString()}`,
    );
  };

  const applyFilters = () => {
    const appliedFilters: StorageCostFilters = { ...draftFilters };
    setDraftFilters(appliedFilters);
    setAppliedFilters(appliedFilters);
  };

  const resetFilters = () => {
    const defaultCostDateFrom = getDefaultCostDateFrom();
    const defaultCostDateTo = getDefaultCostDateTo();
    setCustomerSearchText("");
    const resetState: StorageCostFilters = {
      customerName: null,
      costDateFrom: defaultCostDateFrom,
      costDateTo: defaultCostDateTo,
      searchText: "",
      sortBy: "created_date",
      sortType: SortType.DESC,
    };
    setDraftFilters(resetState);
    setAppliedFilters(resetState);
  };

  const handleSortChange = (column: string, sortType: SortType) => {
    setAppliedFilters((prev) => ({ ...prev, sortBy: column, sortType }));
  };
  const pageInnerSx = {
    marginTop: "0.6rem",
    paddingX: 4,
    paddingY: 2,
    boxShadow:
      "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
    borderRadius: "4px",
    minHeight: { xs: "60vh", md: window.innerHeight - 110 },
  };

  if (!userStore.hasPermission([EmployeeRoles.VIEW_STORAGE_COST])) {
    return <PermissionBlockLabel />;
  }

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      <Paper>
        <Box sx={pageInnerSx}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 0.5,
            }}
          >
            <CustomTypography variant="heading3">
              Storage Cost{" "}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors?.greyTextColor}
            >
              View and manage storage cost summaries by customer and date range.
            </CustomTypography>
          </Box>

          <Paper
            variant="outlined"
            sx={{ mt: 2, borderRadius: 2, borderColor: "divider" }}
          >
            <Box
              onClick={() =>
                storageCostStore.setIsFiltersExpanded(
                  !storageCostStore.isFiltersExpanded,
                )
              }
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 0.5,
                cursor: "pointer",
              }}
            >
              <CustomTypography variant="heading6">Filters</CustomTypography>
              <IconButton size="small">
                {storageCostStore.isFiltersExpanded ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </IconButton>
            </Box>
            <Collapse
              in={storageCostStore.isFiltersExpanded}
              timeout="auto"
              unmountOnExit
            >
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Grid2 container spacing={2} sx={{ flex: 1, width: "100%" }}>
                  <Grid2
                    size={{ xs: 12, sm: 6, md: 3 }}
                    sx={{ minWidth: "220px" }}
                  >
                    <Box sx={{ minWidth: "220px" }}>
                      <DateRangePickerNew
                        placeholder="Cost Date Range"
                        startDate={draftFilters.costDateFrom}
                        endDate={draftFilters.costDateTo}
                        onRangeSelect={(range) => {
                          if (!range.startDate || !range.endDate) return;
                          setDraftFilters((prev) => ({
                            ...prev,
                            costDateFrom: range.startDate,
                            costDateTo: range.endDate,
                          }));
                        }}
                      />
                    </Box>
                  </Grid2>
                  <Grid2
                    size={{ xs: 12, sm: 6, md: 3 }}
                    sx={{ minWidth: "220px" }}
                  >
                    <AppSearchDropDown
                      textFieldValue={customerSearchText}
                      title="Customer Name"
                      isMultiSelect={false}
                      value={selectedCustomerOption}
                      fontSize="12px"
                      list={storageCostStore.customerOptions}
                      local={false}
                      handleSearch={handleCustomerSearch}
                      handleSelect={(items) => {
                        setCustomerSearchText("");
                        setDraftFilters((prev) => ({
                          ...prev,
                          customerName:
                            items.length > 0 ? items[0].value : null,
                        }));
                      }}
                    />
                  </Grid2>
                </Grid2>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1.5,
                    width: { xs: "100%", md: "auto" },
                    alignSelf: { xs: "stretch", md: "flex-end" },
                  }}
                >
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={applyFilters}
                    customStyles={{ minWidth: 140, height: 40, width: "100%" }}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear All"
                    onClick={resetFilters}
                    customStyles={{ minWidth: 140, height: 40, width: "100%" }}
                  />
                </Box>
              </Box>
            </Collapse>
          </Paper>

          {isLoading && (
            <Container
              sx={{
                minHeight: "35vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Container>
          )}

          {!isLoading && (
            <Box mt={3}>
              <StorageCostListingTable
                rows={storageCostStore.records || []}
                sortBy={appliedFilters.sortBy}
                sortType={appliedFilters.sortType || SortType.DESC}
                onSortChange={handleSortChange}
                onOpenDetail={openDetail}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Grid2>
  );
};

export default observer(StorageCostListing);
